import React, { useState, useEffect, useRef } from "react";
import { fetchContent, adminUpdateContent, adminResetContent, adminSendTestEmail, uploadImage } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Loader2, Save, RotateCcw, Mail, Send, Inbox, Upload, X, Image as ImageIcon } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1";

const Section = ({ title, hint, children }) => (
  <div className="bg-white border border-black/10 p-6 mb-6">
    <h3 className="font-serif-display text-lg font-bold gpm-text-green mb-1">{title}</h3>
    {hint && <p className="text-xs text-gray-500 mb-4">{hint}</p>}
    {children}
  </div>
);

const buildPreview = (c) => {
  if (!c) return "";
  const fill = (t) => (t || "").replace(/\{name\}/g, "Jane Smith").replace(/\{carTitle\}/g, "1965 Aston Martin DB5");
  return [fill(c.greeting), "", fill(c.introEnquiry), "", fill(c.body), "", fill(c.signature)].join("\n");
};

const EmailEditor = () => {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bannerRef = useRef(null);
  const { toast } = useToast();

  const load = () => fetchContent("emailReply").then(setC).catch(() => setC(null));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const uploadBanner = async (files) => {
    const f = (files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(f);
      set("bannerUrl", url);
      toast({ title: "Banner uploaded" });
    } catch (e) {
      toast({ title: "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
      if (bannerRef.current) bannerRef.current.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...c };
      delete payload.key;
      await adminUpdateContent("emailReply", payload);
      toast({ title: "Emails saved", description: "Your wording is now live." });
    } catch (err) {
      toast({ title: "Save failed", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all email wording to the defaults? This cannot be undone.")) return;
    const d = await adminResetContent("emailReply");
    setC(d);
    toast({ title: "Reset to default" });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const payload = { ...c };
      delete payload.key;
      await adminUpdateContent("emailReply", payload); // save first so the test uses current wording
      const res = await adminSendTestEmail();
      toast({ title: "Test emails sent", description: `Check ${res.recipient} for the alert + auto-reply previews.` });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send test email.";
      toast({ title: "Test failed", description: msg, variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  if (!c) return <div className="py-20 text-center text-gray-500" data-testid="email-editor-loading">Loading emails…</div>;

  return (
    <div className="max-w-3xl" data-testid="email-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif-display text-2xl font-bold gpm-text-green">Emails</h2>
          <p className="text-sm text-gray-500">Edit the auto-reply customers receive and the alert that lands in your inbox.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button data-testid="email-test-btn" onClick={sendTest} disabled={testing} className="flex items-center gap-2 px-4 py-2.5 border-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: "var(--gpm-gold)", color: "var(--gpm-green)" }}>
            {testing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send me a test
          </button>
          <button data-testid="email-reset-btn" onClick={reset} className="flex items-center gap-2 px-4 py-2.5 border border-black/20 text-sm font-semibold"><RotateCcw size={15} /> Reset</button>
          <button data-testid="email-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
          </button>
        </div>
      </div>

      {/* ---------------- Customer auto-reply ---------------- */}
      <div className="flex items-center gap-2 mb-3 mt-2">
        <Mail size={18} className="gpm-text-green" />
        <h3 className="font-serif-display text-xl font-bold gpm-text-green">Customer Auto-Reply</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">Sent automatically to the customer after they submit an enquiry.</p>

      <div className="mb-6 flex items-start gap-3 border border-black/10 bg-[#f4f2ea] px-4 py-3 text-sm text-gray-700">
        <Mail size={18} className="gpm-text-green shrink-0 mt-0.5" />
        <span>Use <code className="bg-white px-1 border border-black/10">{"{name}"}</code> to insert the customer's name and <code className="bg-white px-1 border border-black/10">{"{carTitle}"}</code> for the car they enquired about. Filled in automatically per email.</span>
      </div>

      <Section title="Header banner" hint="A photo shown across the top of the auto-reply email. Landscape images work best.">
        {c.bannerUrl ? (
          <div className="relative group mb-3 max-w-md">
            <img src={c.bannerUrl.startsWith("http") ? c.bannerUrl : `${process.env.REACT_APP_BACKEND_URL}${c.bannerUrl}`} alt="Email banner" className="w-full aspect-[3/1] object-cover border border-black/10" />
            <button type="button" data-testid="banner-remove" onClick={() => set("bannerUrl", "")} className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} /></button>
          </div>
        ) : (
          <div className="mb-3 flex items-center gap-2 text-sm text-gray-400 border border-dashed border-black/15 px-4 py-6 justify-center"><ImageIcon size={16} /> No banner set</div>
        )}
        <div className="flex items-center gap-3">
          <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(e.target.files)} />
          <button type="button" data-testid="banner-upload" onClick={() => bannerRef.current?.click()} disabled={uploading} className="flex items-center gap-2 px-4 py-2 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {c.bannerUrl ? "Replace banner" : "Upload banner"}
          </button>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="email-show-banner" type="checkbox" checked={c.showBanner !== false} onChange={(e) => set("showBanner", e.target.checked)} />
            Show banner in email
          </label>
        </div>
      </Section>

      <Section title="Subject line">
        <input data-testid="email-subject" className={inputCls} value={c.subject || ""} onChange={(e) => set("subject", e.target.value)} />
      </Section>

      <Section title="Greeting" hint="The opening line. Tip: keep {name} so it greets each person personally.">
        <input data-testid="email-greeting" className={inputCls} value={c.greeting || ""} onChange={(e) => set("greeting", e.target.value)} />
      </Section>

      <Section title="Opening line by enquiry type" hint="The first sentence changes depending on how the customer contacted you.">
        <div className="space-y-4">
          <div><label className={labelCls}>Car enquiry (can use {"{carTitle}"})</label><textarea data-testid="email-intro-enquiry" rows={2} className={inputCls} value={c.introEnquiry || ""} onChange={(e) => set("introEnquiry", e.target.value)} /></div>
          <div><label className={labelCls}>Car sourcing request</label><textarea data-testid="email-intro-sourcing" rows={2} className={inputCls} value={c.introSourcing || ""} onChange={(e) => set("introSourcing", e.target.value)} /></div>
          <div><label className={labelCls}>General contact message</label><textarea data-testid="email-intro-default" rows={2} className={inputCls} value={c.introDefault || ""} onChange={(e) => set("introDefault", e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Main message" hint="The body of the email. Blank lines create new paragraphs.">
        <textarea data-testid="email-body" rows={6} className={inputCls} value={c.body || ""} onChange={(e) => set("body", e.target.value)} />
      </Section>

      <Section title="Sign-off">
        <textarea data-testid="email-signature" rows={3} className={inputCls} value={c.signature || ""} onChange={(e) => set("signature", e.target.value)} />
      </Section>

      <Section title="Branding">
        <div className="space-y-3">
          <label className="flex items-center gap-3 text-sm">
            <input data-testid="email-show-logo" type="checkbox" checked={c.showLogo !== false} onChange={(e) => set("showLogo", e.target.checked)} />
            Show the <b>GPM Classics logo</b> at the bottom of the auto-reply email
          </label>
          <label className="flex items-center gap-3 text-sm">
            <input data-testid="email-show-car" type="checkbox" checked={c.showCar !== false} onChange={(e) => set("showCar", e.target.checked)} />
            Show the <b>car the customer enquired about</b> (photo + price) inside the email
          </label>
        </div>
      </Section>

      <Section title="Live preview" hint="Example of a car enquiry from “Jane Smith” about a “1965 Aston Martin DB5”.">
        <div data-testid="email-preview" className="border border-black/10 bg-white p-5 text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-2 pb-2 border-b border-black/5">Subject: {c.subject}</div>
          {c.showBanner !== false && c.bannerUrl && <img src={c.bannerUrl.startsWith("http") ? c.bannerUrl : `${process.env.REACT_APP_BACKEND_URL}${c.bannerUrl}`} alt="" className="w-full aspect-[3/1] object-cover mb-4" />}
          {buildPreview(c)}
          {c.showCar !== false && (
            <div className="border border-black/10 bg-[#faf9f5] my-4 max-w-xs">
              <div className="w-full aspect-[3/2] bg-gray-200 flex items-center justify-center text-gray-400 text-xs">Car photo</div>
              <div className="p-3">
                <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">The car you enquired about</div>
                <div className="font-bold gpm-text-green text-sm">1965 Aston Martin DB5</div>
                <div className="text-xs font-bold" style={{ color: "var(--gpm-gold)" }}>£POA</div>
              </div>
            </div>
          )}
          {c.showLogo !== false && <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_c3178930-b50b-41f7-96c7-0a33cbb500d8/artifacts/w1cdhpo2_dd9b5887-be1d-4646-b30a-b14ef62aeaf7.png" alt="GPM Classics" className="w-16 mt-5 pt-4 border-t border-black/5" />}
        </div>
      </Section>

      {/* ---------------- Lead alert to you ---------------- */}
      <div className="flex items-center gap-2 mb-3 mt-10">
        <Inbox size={18} className="gpm-text-green" />
        <h3 className="font-serif-display text-xl font-bold gpm-text-green">Lead Alert (to your inbox)</h3>
      </div>
      <p className="text-sm text-gray-500 mb-4">The notification email you receive whenever someone submits a form. The customer's details are added automatically below your intro line.</p>

      <Section title="Alert wording">
        <div className="space-y-4">
          <div><label className={labelCls}>Subject prefix</label><input data-testid="alert-subject-prefix" className={inputCls} value={c.alertSubjectPrefix || ""} onChange={(e) => set("alertSubjectPrefix", e.target.value)} /><p className="text-xs text-gray-400 mt-1">Example subject: “{c.alertSubjectPrefix || "[GPM Classics]"} New Car Enquiry — Jane Smith”</p></div>
          <div><label className={labelCls}>Intro line (can use {"{label}"} for the enquiry type)</label><textarea data-testid="alert-intro" rows={2} className={inputCls} value={c.alertIntro || ""} onChange={(e) => set("alertIntro", e.target.value)} /></div>
          <div><label className={labelCls}>Footer line</label><input data-testid="alert-footer" className={inputCls} value={c.alertFooter || ""} onChange={(e) => set("alertFooter", e.target.value)} /></div>
        </div>
      </Section>

      <div className="flex justify-end pb-6 gap-3">
        <button data-testid="email-test-btn-bottom" onClick={sendTest} disabled={testing} className="flex items-center gap-2 px-5 py-3 border-2 text-sm font-semibold disabled:opacity-60" style={{ borderColor: "var(--gpm-gold)", color: "var(--gpm-green)" }}>
          {testing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send me a test
        </button>
        <button data-testid="email-save-btn-bottom" onClick={save} disabled={saving} className="flex items-center gap-2 px-6 py-3 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save Changes
        </button>
      </div>
    </div>
  );
};

export default EmailEditor;
